/* import __COLOCATED_TEMPLATE__ from './hsv-color-picker.hbs.js'; */
import { action, computed } from '@ember/object';
/* eslint square/no-restricted-files:"off" -- unscoped components in this folder are discouraged */
import { htmlSafe } from '@ember/template';
import Component from '@ember/component';
import tinycolor from 'tinycolor2';

/**
## hsv color picker
`hsv-color-picker` provides a basic color picker similar to what you would see in
graphics software like Photoshop. The 3 axes you can control in this component
map to the HSV color space (the box controls Saturation and Value and the slider
controls Hue). However, the `onchange` event fired from the event provides an
rgb hex string e.g. `49a4d5`

```example
@title Basic Example

@hbs
{{hsv-color-picker color=color onchange=(action (mut color))}}
```

### Parameters
@param {string} [color] The color that should be displayed in the color picker
@param {action} [onchange] This action is sent when the user changes something
*/

const DEFAULT_COLOR = '546476';

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  classNames: ['hsv-color-picker'],
  color: DEFAULT_COLOR,
  didReceiveAttrs() {
    this._super();
    const newColor = tinycolor(this.color || DEFAULT_COLOR);
    const currentColor = tinycolor(this.hsv);
    // Using manual 'hsv' instead of computing it allows for more rendering smoothness
    // because it has higher fidelity than this.color's rgb hex
    if (!this.hsv || currentColor.toHex() !== newColor.toHex()) {
      this.set('hsv', newColor.toHsv());
    }
  },
  dotStyle: computed('hsv', function () {
    const hsv = this.hsv;
    const left = `${hsv.s * 100}%`;
    const top = `${100 - hsv.v * 100}%`;
    const borderColor = hsv.v <= 0.7 ? '#fff' : '#000';
    return htmlSafe(`left: ${left}; top: ${top}; border-color: ${borderColor};`);
  }),
  backgroundStyle: computed('hsv.h', function () {
    const color = tinycolor({
      h: this.hsv.h,
      s: 1,
      v: 1
    });
    return htmlSafe(`background: linear-gradient(to right, #fff, ${color.toRgbString()})`);
  }),
  willDestroyElement(...args) {
    this._super(...args);
    this.stopListening();
  },
  mousedown: action(function (e) {
    this.set('paletteDiv', e.currentTarget);
    this.handleMouseMove(e);
    this.stopListening();
    window.addEventListener('mousemove', this.handleMouseMove);
    window.addEventListener('mouseup', this.stopListening);
  }),
  stopListening: action(function () {
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mouseup', this.stopListening);
  }),
  handleMouseMove: action(function (e) {
    const clientRects = this.paletteDiv.getClientRects()[0];
    const s = (e.clientX - clientRects.left) / clientRects.width;
    const v = 1 - (e.clientY - clientRects.top) / clientRects.height;
    const hsv = {
      h: this.hsv.h,
      s: Math.min(Math.max(s, 0), 1),
      v: Math.min(Math.max(v, 0), 1)
    };
    this.set('hsv', hsv);
    this.onchange?.(tinycolor(hsv).toHex());
  }),
  changeHue: action(function (hue) {
    const hsv = {
      ...this.hsv,
      h: hue
    };
    this.set('hsv', hsv);
    this.onchange?.(tinycolor(hsv).toHex());
  })
});